import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElementContainer, StripeCardElementForm } from './StripeCardElement.styled';
import Keyboard from '../../../api/keyboard';
import StripeCard from '../../../components/StripeCard/StripeCard';
import { CustomButton, SupportedCurrency } from '@mindly/ui-components';
import {
  NewCardButton,
  NewCardTitle,
  SavedCardsWrapper,
} from '../../PaymentPage/CardFormComponent/CardFormComponent.style';
import { useDispatch, useSelector } from 'react-redux';
import { uniqSavedCreditCardsSelector } from '../../../redux/savedCreditCards/selector';
import { SavedCreditCards } from '../../../components/SavedCreditCards';
import { sortCardsByDefaultPaymentMethod } from '../../PaymentPage/CardFormComponent/utils';
import { SavedCreditCardsType } from '../../PaymentPage/type';
import { confirmSubscription, CreateSubscriptionReturnType } from '../../../api/subscription';
import { loadSavedCreditCards } from '../../../redux/savedCreditCards/action';
import { userSelector } from '../../../redux/user/selector';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatErrorMessage } from '../../../utils/formatErrorMessage';
import ErrorPaymentModal from '../ErrorPaymentModal';
import useToast from '../../../context/ToastContext/ToastContext';
import { backendErrorCodes, stripeErrorCodes } from '../../../context/ToastContext/types';
import { CouponPreviewDetailsType, useAnalytics } from '../../../shared';
import { PaymentProvidersFeature } from 'src/features/PaymentProvidersFeature';
import { ISpecialist } from 'src/redux/specialists/types';

type StripeCardElementProps = {
  sessionPrice: string;
  setFetchingStatus: (fetchingStatus: null | 'start' | 'success') => void;
  setPaymentStatus: (status: null | 'confirmed' | 'paid' | 'unknown') => void;
  setSubscriptionID: (flag: string | null) => void;
  subscriptionInfo: CreateSubscriptionReturnType;
  couponPreviewDetails?: CouponPreviewDetailsType | null;
  specialist: ISpecialist;
  disabled?: boolean;
};

const StripeCardElement: React.FC<StripeCardElementProps> = ({
  setFetchingStatus,
  setPaymentStatus,
  setSubscriptionID,
  subscriptionInfo,
  couponPreviewDetails,
  disabled,
  specialist,
}) => {
  const dispatch = useDispatch();

  const stripe = useStripe();
  const elements = useElements();
  const { showToast } = useToast();
  const intl = useIntl();

  const [addNewCard, setAddNewCard] = useState(false);

  const [selectedCreditCard, setSelectedCreditCard] = useState<SavedCreditCardsType>({
    brand: '',
    exp_month: 0,
    exp_year: 0,
    last4: '',
    paymentMethodId: '',
    isDefaultPaymentMethod: false,
  });

  const [isCardSelected, setIsCardSelected] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const cardInputRef = useRef<HTMLDivElement | null>(null);
  const savedCreditCards = useSelector(uniqSavedCreditCardsSelector);
  const user = useSelector(userSelector);

  const { trackPredefinedEvent } = useAnalytics();

  const createPaymentMethod = useCallback(async () => {
    const cardElements = elements?.getElement(CardElement);

    if (!cardElements)
      return showToast({
        text: 'Щось пішло не так, схоже дані про оплату не завантажились, спробуйте ще раз та зв`яжіться з тех.підтрмкою',
      });

    const { paymentMethod, error: stripeError } =
      (await stripe?.createPaymentMethod({
        type: 'card',
        card: cardElements,
      })) ?? {};

    if (stripeError) return showToast({ error: stripeError });

    return paymentMethod?.id;
  }, [stripe, elements]);

  const trackPaymentButtonClick = useCallback(
    (paymentType: 'card' | 'apple' | 'google') => {
      if (!specialist) return;

      let paymentTypeToTrack;
      switch (paymentType) {
        case 'apple':
          paymentTypeToTrack = 'Apple Pay';
          break;
        case 'google':
          paymentTypeToTrack = 'Google Pay';
          break;
        default:
          paymentTypeToTrack = 'Card';
          break;
      }
      trackPredefinedEvent('SUBSCRIPTION_CHECKOUT_PAY_BUTTON_CLICKED', {
        specialist_id: specialist.id,
        specialist_name: `${specialist.first_name} ${specialist.last_name}`,
        payment_type: paymentTypeToTrack,
      });
    },
    [specialist, trackPredefinedEvent]
  );

  const formSubmitHandler = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!stripe) {
        setFetchingStatus(null);

        return showToast({
          text: `Сервіс для оплати ще не загрузився, спробуйте сплатити через 10 секунд, або перезагрузіть сторінку.`,
        });
      }

      try {
        setFetchingStatus('start');

        trackPaymentButtonClick('card');
        const paymentMethod = selectedCreditCard?.paymentMethodId?.length
          ? selectedCreditCard?.paymentMethodId
          : await createPaymentMethod();

        if (!paymentMethod) return setFetchingStatus(null);

        const cards = user?.id && (await dispatch(loadSavedCreditCards(user?.id)));

        cards?.length && setSelectedCreditCard(cards.find((el: SavedCreditCardsType) => el.isDefaultPaymentMethod));

        const { clientSecret, paymentType } = await confirmSubscription(subscriptionInfo.subscription_id, {
          couponPreview: couponPreviewDetails,
        });

        if (paymentType !== 'coupon') {
          const resultOfConfirm = await stripe.confirmCardPayment(clientSecret, {
            payment_method: paymentMethod,
          });

          if (resultOfConfirm.error) {
            setErrorMessage(
              stripeErrorCodes(intl)[resultOfConfirm.error.code as Partial<keyof typeof stripeErrorCodes>].text
            );
            setFetchingStatus(null);
            return;
          }
        }

        setPaymentStatus('confirmed');
        setSubscriptionID(subscriptionInfo?.subscription_id ?? null);
      } catch (error: any) {
        setFetchingStatus(null);
        setErrorMessage(formatErrorMessage(error, backendErrorCodes(intl), intl));
        return;
      }
    },
    [stripe, elements, selectedCreditCard, subscriptionInfo, user, couponPreviewDetails]
  );

  const handleFocus = () => {
    setSelectedCreditCard({
      brand: '',
      exp_month: 0,
      exp_year: 0,
      last4: '',
      paymentMethodId: '',
      isDefaultPaymentMethod: false,
    });
  };

  const paymentProviderLabel = useMemo(() => {
    const subscriptionInterval = subscriptionInfo?.subscription_interval_in_month;
    const labelMap = {
      '1': 'Monthly therapy subscription',
      '3': 'Quarterly therapy subscription',
      '6': 'Semi-annual therapy subscription',
    };

    return labelMap[(subscriptionInterval ?? '1') as keyof typeof labelMap];
  }, []);

  useEffect(() => {
    Keyboard?.addListener('keyboardDidShow', () => {
      if (document.activeElement?.tagName === 'IFRAME')
        cardInputRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'start',
        });
    });

    return () => {
      Keyboard?.removeAllListeners();
    };
  }, []);

  useEffect(() => {
    if (selectedCreditCard?.brand) {
      setIsCardSelected(true);
    }
  }, [selectedCreditCard]);

  return (
    <>
      <StripeCardElementContainer>
        <PaymentProvidersFeature
          stripeChargeCurrency={subscriptionInfo.price_type as unknown as SupportedCurrency}
          stripeChargeAmount={couponPreviewDetails?.payment_amount_charged ?? subscriptionInfo.amount}
          label={paymentProviderLabel}
          disabled={disabled}
          onClick={(type) => {
            trackPaymentButtonClick(type);
          }}
          onSuccess={() => {
            setFetchingStatus('start');
            setPaymentStatus('confirmed');
            setSubscriptionID(subscriptionInfo?.subscription_id ?? null);
          }}
          usePaymentRequest={true}
          confirmPayment={async () => {
            const result = await confirmSubscription(subscriptionInfo.subscription_id, {
              couponPreview: couponPreviewDetails,
            });
            return result;
          }}
        />

        {!!savedCreditCards?.length && (
          <SavedCardsWrapper>
            <SavedCreditCards
              savedCreditCards={sortCardsByDefaultPaymentMethod(savedCreditCards)}
              setChoosedCreditCard={setSelectedCreditCard}
              choosedCreditCard={selectedCreditCard}
            />
          </SavedCardsWrapper>
        )}
        {!addNewCard && !!savedCreditCards?.length && (
          <NewCardButton onClick={() => setAddNewCard(true)} disabled={disabled}>
            <FormattedMessage id={'card.pay.new'} defaultMessage={'Або оплатити новою карткою'} /> <span>+</span>
          </NewCardButton>
        )}
        <StripeCardElementForm onSubmit={formSubmitHandler}>
          {(savedCreditCards?.length === 0 || addNewCard) && (
            <div>
              {!!savedCreditCards?.length && (
                <NewCardTitle>
                  <FormattedMessage id={'card.pay.new'} defaultMessage={'Або оплатити новою карткою'} />
                </NewCardTitle>
              )}
              <StripeCard
                ref={cardInputRef}
                onFocus={handleFocus}
                onChange={(e) => {
                  setIsCardSelected(e.complete ? true : false);
                }}
              />
            </div>
          )}
          <CustomButton buttonType="primary" disabled={!isCardSelected || disabled}>
            <FormattedMessage id={'card.pay'} defaultMessage={'Сплатити'} />
          </CustomButton>
        </StripeCardElementForm>
      </StripeCardElementContainer>
      <ErrorPaymentModal isOpen={!!errorMessage} onClose={() => setErrorMessage('')} message={errorMessage} />
    </>
  );
};

export default StripeCardElement;
